package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.onScroll
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.site.api.Async
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlin.coroutines.resume
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.rememberLPresence
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import com.varabyte.kobweb.browser.dom.observers.ResizeObserver

data class KeyListPaneModel(
    val locale: String,
    val project: ProjectDetailDto?,
    val rows: List<KeyRow>,
    val total: Int,
    val selectedKeyId: String?,
    val search: String,
    val selectedStateIndex: Int,
    val fileId: String,
    val tag: String,
    /** Set only by a work-queue link; there is no control for it in the filter bar. */
    val review: ReviewStatus? = null,
    /** Set only by a work-queue link; there is no control for it in the filter bar. */
    val stale: Boolean = false,
    val severityByKey: Map<String, QaSeverity>,
    /** Keys with a save nobody has resolved yet — see `TranslationEditorState.conflicts`. */
    val conflictKeyIds: Set<String> = emptySet(),
    val loading: Boolean,
    val loadingMore: Boolean,
    val error: String?,
    val errorStatus: Int? = null,
    val pagingError: String? = null,
    val activePluralCategory: PluralCategory? = null,
    val showTargetPreview: Boolean = false,
    val canImport: Boolean = false,
) {
    val hasNoKeysAtAll: Boolean
        get() = (project?.keyCount ?: 0) == 0

    fun narrowestFilterDescription(): String = narrowestEditorFilterDescription(
        locale = locale,
        search = search,
        tag = tag,
        fileId = fileId,
        selectedStateIndex = selectedStateIndex,
        review = review,
        stale = stale,
    )

    // Counted through `tPlural` and named through the catalogue. The count and the language were
    // interpolated into an English sentence, which is two problems in one line: the sentence could
    // not be translated, and "1 strings" is English's own rule getting itself wrong.
    fun arrivingFilterDescription(): String? = when {
        review == ReviewStatus.CHANGES_REQUESTED -> tPlural(
            "editor.key_list_pane.showing_changes_requested",
            total,
            "Showing {count} in {locale} with changes requested.",
            "Showing {count} in {locale} with changes requested.",
            "locale" to locale,
        )
        review != null -> tPlural(
            "editor.key_list_pane.showing_review_state",
            total,
            "Showing {count} in {locale} in one review state.",
            "Showing {count} in {locale} in one review state.",
            "locale" to locale,
        )
        stale -> tPlural(
            "editor.key_list_pane.showing_stale",
            total,
            "Showing {count} in {locale} whose English has changed since they were written.",
            "Showing {count} in {locale} whose English has changed since they were written.",
            "locale" to locale,
        )
        else -> null
    }
}

/**
 * Which one filter emptied the queue, in a sentence.
 *
 * Reading four filters back to somebody is the same as saying "no results" — they have already
 * looked at the filter bar twice. One clause is something to act on.
 *
 * Shared rather than a method, because the editor's *other* zero-row surface — the translation pane
 * with nothing selected — said only "Nothing in de matches what you have narrowed to." for the same
 * state, so which of the two panes a reader happened to be looking at decided whether the product
 * named the culprit.
 */
internal fun narrowestEditorFilterDescription(
    locale: String,
    search: String,
    tag: String,
    fileId: String,
    selectedStateIndex: Int,
    review: ReviewStatus?,
    stale: Boolean,
): String = when {
    review != null ->
        trf("editor.key_list_pane.empty_review_state", "No strings in {locale} are in that review state.", "locale" to locale)
    stale ->
        trf("editor.key_list_pane.empty_stale", "Nothing in {locale} is behind the source.", "locale" to locale)
    search.isNotBlank() -> trf(
        "editor.key_list_pane.empty_search",
        "Nothing matches \"{search}\" in {locale}.",
        "search" to search,
        "locale" to locale,
    )
    tag.isNotBlank() ->
        trf("editor.key_list_pane.empty_tag", "No keys carry the tag \"{tag}\".", "tag" to tag)
    fileId.isNotBlank() ->
        trf("editor.key_list_pane.empty_file", "That file has no strings in {locale}.", "locale" to locale)
    selectedStateIndex > 0 ->
        trf("editor.key_list_pane.empty_state", "No strings are in that state in {locale}.", "locale" to locale)
    else -> tr("editor.key_list_pane.empty_filters", "Nothing matches the current filters.")
}

@Composable
internal fun KeyListPane(
    state: TranslationEditorState,
    queueMode: EditorQueueMode,
    renderedLayout: EditorRenderedLayout,
    persistedScrollTopPx: Double,
    showTargetPreview: Boolean,
    rowsAppliedRevision: Long?,
    currentQueryRevision: Long,
    onRecordScroll: (Double) -> Unit,
    onRowsApplied: (Long) -> Unit,
    onSelectKey: (String) -> Unit,
    onOpenFilters: (EditorFocusRef) -> Unit,
    onCloseFilters: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * How the queue's own automations ask for a decision.
     *
     * They go through the editor's modal surface rather than a dialog of their own so Escape, the
     * focus trap, the scrim and the return-focus contract are the ones already tested here — an
     * independent dialog inside the queue would be the second focus owner on a screen whose whole
     * layout machinery assumes there is one.
     */
    onOpenBulkConfirm: (EditorFocusRef) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    val router = rememberPageContext().router
    val remoteSeverityByKey by remember(state) {
        derivedStateOf {
            state.qaIssues
                .groupBy { it.keyId }
                .mapValues { (_, issues) -> highestSeverity(issues) }
        }
    }
    val severityByKey = state.rows.associate { row ->
        val draft = state.drafts[row.key.id]
        val liveSeverity = if (draft?.dirty == true) {
            inlineQa(row, draft, state.locale).highestSeverity()
        } else {
            null
        }
        row.key.id to strongestSeverity(remoteSeverityByKey[row.key.id], liveSeverity)
    }.mapNotNull { (keyId, severity) -> severity?.let { keyId to it } }.toMap()
    val model = KeyListPaneModel(
        locale = state.locale,
        project = state.project,
        rows = state.rows.toList(),
        total = state.total,
        selectedKeyId = state.selectedKeyId,
        search = state.search,
        selectedStateIndex = EditorStateFilter.entries.indexOf(state.stateFilter).coerceAtLeast(0),
        fileId = state.fileId,
        tag = state.tag,
        review = state.review,
        stale = state.stale,
        severityByKey = severityByKey,
        conflictKeyIds = state.conflicts.keys,
        loading = state.keysLoading,
        loadingMore = state.loadingMore,
        error = state.keysError,
        errorStatus = state.keysErrorStatus,
        pagingError = state.keysPagingError,
        activePluralCategory = state.pluralCategory,
        showTargetPreview = showTargetPreview,
        canImport = state.capability.canWriteTranslations && state.canEditStructure,
    )

    Div(
        KeyPaneStyle.toModifier()
            .then(modifier)
            .attr("data-editor-queue", "true")
            // The queue is how somebody moves between strings, so it is navigation — and naming it
            // is what lets a screen reader jump to it instead of arrowing through the whole run.
            .attr("role", "navigation")
            .attr("aria-label", t("editor.key_list_pane.translation_queue", "Translation queue"))
            .toAttrs(),
    ) {
        CompactQueueHeader(
            state = state,
            queueMode = queueMode,
            onOpenFilters = onOpenFilters,
            onOpenBulkConfirm = onOpenBulkConfirm,
        )
        val list: @Composable () -> Unit = {
            EditorQueueListBody(
                model = model,
                persistedScrollTopPx = persistedScrollTopPx,
                rowsAppliedRevision = rowsAppliedRevision,
                currentQueryRevision = currentQueryRevision,
                onRecordScroll = onRecordScroll,
                onRowsApplied = onRowsApplied,
                onSelectKey = onSelectKey,
                onLoadMore = { state.loadMore() },
                onRetry = { scope.launch { state.loadKeys() } },
                onClearFilters = state::clearFilters,
                onImport = { slug -> router.tryRoutingTo("/app/projects/$slug/data") },
            )
        }
        val filters: @Composable () -> Unit = {
            EditorQueueFilters(state = state, onDone = onCloseFilters)
        }
        if (renderedLayout == EditorRenderedLayout.COMPACT) {
            // The compact layout keeps the replacement behaviour: there is genuinely not room for
            // both the list and the filter surface on screen at once.
            EditorQueueModeBody(mode = queueMode, list = list, filters = filters)
        } else {
            // Selecting a filter used to hide the list it filters, so every choice was blind until
            // "Done" was pressed. On the workbench there is room for both — the filter surface
            // sits above the list rather than replacing it.
            EditorQueueStackedBody(mode = queueMode, list = list, filters = filters)
        }
    }
}

/**
 * The workbench counterpart to [EditorQueueModeBody]: filters, when open, sit above the list rather
 * than replacing it. Kept as its own function rather than a branch inside [EditorQueueModeBody] so
 * that function's own tested either/or contract — exactly one of `list`/`filters` mounted — stays
 * true for the compact layout that still needs it.
 */
@Composable
internal fun EditorQueueStackedBody(
    mode: EditorQueueMode,
    list: @Composable () -> Unit,
    filters: @Composable () -> Unit,
) {
    Div(
        Modifier
            .attr("data-editor-queue-mode", mode.name.lowercase())
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("flex", "1 1 auto")
                property("min-height", "0")
                property("width", "100%")
                property("overflow", "hidden")
            }
            .toAttrs(),
    ) {
        // **Presence, so the panel has an exit.** A bare `if` gave it one state and no transition
        // could run; it appeared and vanished between frames. The list is untouched — it is not the
        // thing arriving, and the contract forbids animating queue measurements.
        val filtersPresence = rememberLPresence(mode == EditorQueueMode.FILTERS, Motion.FastMs)
        if (filtersPresence.rendered) {
            CompositionLocalProvider(LocalQueueFilterPhase provides filtersPresence.phase) {
                filters()
            }
        }
        list()
    }
}

/**
 * The presence phase the filter surface stamps onto itself.
 *
 * A local rather than a parameter because `filters` is a `@Composable () -> Unit` slot: threading a
 * phase through it would change the shape of a lambda three call sites pass, to carry a value only
 * the innermost element reads.
 */
internal val LocalQueueFilterPhase = compositionLocalOf { LPresencePhase.PRESENT }

@Composable
private fun EditorQueueListBody(
    model: KeyListPaneModel,
    persistedScrollTopPx: Double,
    rowsAppliedRevision: Long?,
    currentQueryRevision: Long,
    onRecordScroll: (Double) -> Unit,
    onRowsApplied: (Long) -> Unit,
    onSelectKey: (String) -> Unit,
    onLoadMore: () -> Unit,
    onRetry: () -> Unit,
    onClearFilters: () -> Unit,
    onImport: (String) -> Unit,
) {
    // **Measured, not guessed.**
    //
    // This was a flat `900.0` corrected only inside `onScroll`, so the virtualised window was
    // computed against 900px until the reader scrolled — on first paint always, and after a window
    // resize until the next scroll. On a tall column that renders too few rows, so the list ends in
    // blank space that fills in as you scroll; on a short one it renders far more than fit. Both
    // read as the list not knowing its own size, which is what "the scroll is weird" describes.
    //
    // A `ResizeObserver` covers first paint, window resize and remount with one mechanism, and it
    // fires before paint rather than in answer to an event the reader had to generate.
    var viewportHeight by remember { mutableStateOf(0.0) }

    DisposableEffect(Unit) {
        val scroller = document.getElementById(KEY_SCROLLER_ID) as? HTMLElement
        if (scroller == null) {
            onDispose { }
        } else {
            viewportHeight = scroller.clientHeight.toDouble()
            val observer = ResizeObserver {
                val measured = scroller.clientHeight.toDouble()
                if (measured > 0.0) viewportHeight = measured
            }
            observer.observe(scroller)
            onDispose { observer.disconnect() }
        }
    }
    val latestRowsCommitted = rememberUpdatedState(newValue = !model.loading)

    LaunchedEffect(rowsAppliedRevision, currentQueryRevision, model.rows.size, EditorQueueMode.LIST) {
        val revision = rowsAppliedRevision ?: return@LaunchedEffect
        suspendCancellableCoroutine<Unit> { continuation ->
            window.requestAnimationFrame {
                if (continuation.isActive) continuation.resume(Unit)
            }
        }
        if (shouldSignalEditorQueueRowsApplied(
                rowsAppliedRevision = revision,
                currentQueryRevision = currentQueryRevision,
                mode = EditorQueueMode.LIST,
                rowsCommitted = latestRowsCommitted.value,
            )
        ) {
            onRowsApplied(revision)
        }
    }

    Div(
        KeyScrollerStyle.toModifier()
            .attr("id", KEY_SCROLLER_ID)
            .attr("data-editor-queue-scroll", "true")
            .onScroll { event ->
                val scroller = event.target as? HTMLElement ?: return@onScroll
                viewportHeight = scroller.clientHeight.toDouble()
                onRecordScroll(scroller.scrollTop)
                val remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight
                if (remaining <= EDITOR_ROW_HEIGHT_PX * EDITOR_LIST_OVERSCAN) onLoadMore()
            }
            .toAttrs(),
    ) {
        when {
            model.loading && model.rows.isEmpty() -> KeyListSkeleton()
            // `AsyncFailureState`, which reads the failure rather than assuming a fault. Both
            // panels beside this one already say so in their own comments: a key a colleague
            // deleted, or a role revoked while the editor was open, is a boundary — and the editor
            // is the surface held open longest, so both are the likely cases here rather than the
            // exotic ones. `ErrorState` offered Retry on each, which re-runs a refused request.
            model.error != null && model.rows.isEmpty() -> AsyncFailureState(
                failure = Async.Failure(model.error, model.errorStatus),
                modifier = Modifier.padding(Space.LG),
                subject = t("editor.key_list_pane.this_key_queue", "this key queue"),
                faultMessage = model.error,
                onRetry = onRetry,
            )
            model.rows.isEmpty() && model.hasNoKeysAtAll -> LEmptyState(
                title = t("editor.key_list_pane.nothing_to_translate_yet", "Nothing to translate yet"),
                description = if (model.canImport) {
                    t("editor.key_list_pane.this_project_has_no_keys_in", "This project has no keys in it. Import a file, or push them from your repository with `lingvo push`.")
                } else {
                    t("editor.key_list_pane.no_keys_are_available_to_translate", "No keys are available to translate yet. They will appear here after an owner or admin imports or pushes them.")
                },
                modifier = Modifier.padding(Space.LG),
                icon = LEmptyStateIcon.CODE,
                action = {
                    if (model.canImport) {
                        model.project?.slug?.let { slug ->
                            LButton(t("editor.key_list_pane.import_a_file", "Import a file"), { onImport(slug) })
                        }
                    }
                },
            )
            model.rows.isEmpty() -> LEmptyState(
                title = t("editor.key_list_pane.no_strings_match_these_filters", "No keys match these filters"),
                description = model.narrowestFilterDescription(),
                modifier = Modifier.padding(Space.LG),
                icon = LEmptyStateIcon.CODE,
                action = {
                    LButton(t("editor.key_list_pane.clear_filters", "Clear filters"), onClearFilters, variant = LButtonVariant.SECONDARY)
                },
            )
            else -> VirtualizedRows(
                model = model,
                virtualScrollTopPx = persistedScrollTopPx.coerceAtLeast(0.0),
                // The floor matters for one frame: before the observer has measured, this is 0,
                // and a window of zero rows would render an empty list under a full scrollbar.
                viewportHeight = viewportHeight.coerceAtLeast(EDITOR_ROW_HEIGHT_PX.toDouble()),
                onSelectKey = onSelectKey,
                onRetryPaging = onLoadMore,
            )
        }
    }
}

@Composable
internal fun EditorQueueModeBody(
    mode: EditorQueueMode,
    list: @Composable () -> Unit,
    filters: @Composable () -> Unit,
) {
    Div(
        Modifier
            .attr("data-editor-queue-mode", mode.name.lowercase())
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("flex", "1 1 auto")
                property("min-height", "0")
                property("width", "100%")
            }
            .toAttrs(),
    ) {
        when (mode) {
            EditorQueueMode.LIST -> list()
            // The panel does not stretch — it must not, see `EditorQueueFilterSurfaceStyle` — so
            // the leftover height is given a ground of its own rather than left as bare `surface`
            // under a hard two-tone seam. See `EditorQueueFilterModeStyle`.
            EditorQueueMode.FILTERS -> Div(EditorQueueFilterModeStyle.toModifier().toAttrs()) {
                filters()
            }
        }
    }
}

internal fun shouldSignalEditorQueueRowsApplied(
    rowsAppliedRevision: Long?,
    currentQueryRevision: Long,
    mode: EditorQueueMode,
    rowsCommitted: Boolean,
): Boolean = rowsAppliedRevision == currentQueryRevision &&
    mode == EditorQueueMode.LIST && rowsCommitted

private fun List<InlineQaFinding>.highestSeverity(): QaSeverity? = when {
    any { it.severity == QaSeverity.ERROR } -> QaSeverity.ERROR
    any { it.severity == QaSeverity.WARNING } -> QaSeverity.WARNING
    else -> null
}

private fun strongestSeverity(first: QaSeverity?, second: QaSeverity?): QaSeverity? = when {
    first == QaSeverity.ERROR || second == QaSeverity.ERROR -> QaSeverity.ERROR
    first == QaSeverity.WARNING || second == QaSeverity.WARNING -> QaSeverity.WARNING
    else -> null
}
